<template>
  <div class="tabulator-table-wrapper">
    <template v-if="editable">
      <div :id="tabulatorId" ref="tabulatorTable" />
      <input
        type="file" :id="fileUploadId" class="tabulator-file-upload-field"
        @change="handleCSVFile($event.target.files)" />
      <div class="tabulator-table-header-hint">
        Right-click to see table options or download table.
      </div>
      <button
        class="btn btn-link tabulator-add-row-button-bottom"
        @click="addRowToBottom">
        <font-awesome-icon class="add-button" :icon="faIcons.faPlus" />
        <em>Add new row</em>
      </button>
    </template>
    <template v-if="!schema.attributes.editing">
      <div ref="tabulatorTable" />
    </template>
    <div
      class="form-input-hint text-error"
      v-if="schema.attributes.editing && disabledForAdminOnly">
      Field is restricted to site administrators.
    </div>
  </div>
</template>

<script>
// This is a lightly modified version of vue-form-generator's input field.
import _ from 'lodash';
import Papa from 'papaparse';

import { MixinDataToReactiveData } from 'assets/js/util';

import { abstractField } from 'components/Widgets/FormGenerator';
import FieldCommon from './FieldCommon';

// This will store the Tabulator class that we dynamically import at first use.
let Tabulator;

export default {
  name: 'field-q-table',

  mixins: [abstractField, FieldCommon],

  setup() {
    const afd = MixinDataToReactiveData(abstractField);
    const fcd = MixinDataToReactiveData(FieldCommon);
    return {
      tableData: [],
      tabulator: null,
      ...afd,
      ...fcd,
    };
  },

  beforeUnmount() {
    this.uninjectTabulatorFn();
  },

  computed: {
    editable() {
      if (!this.schema) return false;
      return this.schema.attributes.editing && !this.disabledForAdminOnly;
    },
  },

  data() {
    const uuid = this.uuid4();
    return {
      columnIds: _.map(this.schema.values, (col) => this.camelize(col)),
      fileUploadId: `csv-uploader-input-${uuid}`,
      headerContextMenu: null,
      tabulatorId: `tabulator-div-${uuid}`,
    };
  },

  emits: [
    'emitTableUpdated',
  ],

  methods: {
    async addRowToBottom() {
      const rowsOld = this.tabulator.getRows();
      const rowLast = rowsOld[rowsOld.length - 1];
      const rowsNew = await this.tabulator.addData([this.arrayToObject(
        this.columnIds, _.identity, () => '')]);
      rowsNew[0].move(rowLast, false);
      this.value = this.serializeCSV();
    },

    camelize(str) {
      return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        if (index === 0) return word.toLowerCase();
        return word.toUpperCase();
      }).replace(/\s+/g, '');
    },

    async copyToClipboard() {
      if (!navigator?.clipboard?.writeText) {
        await this.$swal({
          title: 'Browser Does Not Support Direct Copy',
          input: 'textarea',
          inputValue: this.value,
          text: 'Your browser does not support directly copying to ' +
            'the clipboard to our web application. Please copy your table ' +
            'data from the field below.',
          animation: true,
          showLoaderOnConfirm: false,
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: 'Close',
          allowOutsideClick: false,
        });
      } else {
        navigator.clipboard.writeText(this.value);
      }
    },

    // createColumnArgObj creates a column input argument from a column name.
    createColumnArgObj(columnName, headerContextMenu) {
      return {
        editor: this.editable ? 'input' : false,
        field: this.camelize(columnName),
        headerContextMenu,
        hozAlign: 'left',
        title: columnName,
      };
    },

    downloadCSVTable() {
      // Remove empty unicode character.
      let csvString = this.serializeCSV();
      if (csvString.slice(0, 2) === '﻿"') {
        csvString = csvString.slice(1);
      }

      const downloadLink = document.createElement('a');
      const blob = new Blob(['\ufeff', csvString]);
      const url = URL.createObjectURL(blob);
      downloadLink.href = url;
      downloadLink.download = 'form_table.csv';

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    },

    // handleCSVFile handles an incoming CSV file.
    handleCSVFile(files) {
      this.injectTableDataFromString(files[0], true);
    },

    // injectTabulatorIntoWindow injects the tabulator object into the
    // window so that ReservationBook can access it. The problem is that
    // redraw doesn't work if v-show is false on a parent element (e.g.
    // when in ReservationBook mode and the current stage is not the form),
    // so we need these global values to ensure that the table draws correctly
    // when the user paginates over in the wizard.
    injectTabulatorIntoWindow() {
      if (!_.get(window, 'TABULATOR_TABLES')) {
        window.TABULATOR_TABLES = {};
      }
      _.set(window, `TABULATOR_TABLES.${this.tabulatorId}`, this.tabulator);
    },

    // injectTableDataFromString is the main parser for CSV/TSV strings.
    async injectTableDataFromString(data, withHeaders) {
      let parsed;
      try {
        if (data instanceof File) {
          const readPromise = new Promise((resolve) => {
            Papa.parse(data, {
              skipEmptyLines: true,
              complete: (results) => resolve(results),
            });
          });
          parsed = await readPromise;
        }
        if (data instanceof String || typeof data === 'string') {
          parsed = Papa.parse(_.replace(data, /\t/g, ','), {
            delimiter: ',',
          });
        }
      } catch (err) {
        this.$swal({
          title: 'Error Parsing Pasted Data',
          text: String(err),
          type: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: true,
          showCancelButton: false,
          confirmButtonText: 'OK',
        });
        return;
      }

      if (!parsed) return;
      if (parsed.errors.length) {
        this.$swal({
          title: 'Error Parsing Pasted Data',
          text: String(_.join(_.map(parsed.errors, (err) => err.message),
            ', ')),
          type: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: true,
          showCancelButton: false,
          confirmButtonText: 'OK',
        });
        return;
      }

      // There's a weird issue with tabulator/papaparse where the first cell
      // gets quotes and an invisible character because of file header stuff
      // for CSVs. Strip these quotes.
      if (parsed &&
        parsed.data &&
        withHeaders &&
        _.get(parsed, 'data[0][0]') &&
        parsed.data[0][0].slice(0, 2) === '﻿"' &&
        parsed.data[0][0].slice(-1) === '"') {
        parsed.data[0][0] = parsed.data[0][0].slice(2, -1);
      }

      let headers;
      let rows;
      if (withHeaders) {
        headers = _.get(parsed, 'data[0]');
        rows = _.slice(_.get(parsed, 'data'), 1);
      } else {
        rows = _.get(parsed, 'data');
      }

      // Sometimes the last row is empty, detect this and remove it.
      if (rows &&
        rows.length > 0 &&
        rows.slice(-1)[0].length < rows[0].length) {
        rows.splice(-1);
      }

      if (headers) {
        this.columnIds = _.map(headers, (hdr) => this.camelize(hdr));
        await this.tabulator.setColumns(_.concat(
          [{
            rowHandle: true,
            formatter: 'handle',
            headerSort: false,
            frozen: true,
            width: 30,
            minWidth: 30,
          }],
          _.map(headers, (col) => this.createColumnArgObj(col,
            this.headerContextMenu))));
      }
      if (rows) {
        this.tableData = _.map(rows, (row) =>
          this.arrayToObject(
            this.columnIds, _.identity, (ele, idx) => _.get(row, `[${idx}]`)));
        this.tabulator.replaceData(this.tableData);
      }
      this.tabulator.redraw(true);
      this.value = this.serializeCSV();
    },

    async pasteClipboardAsTable() {
      try {
        await this.$swal({
          title: 'Paste Clipboard as Table',
          text: 'Are you sure you want to paste the current ' +
            'clipboard as a new table? All current ' +
            'headers and cells will be replaced.',
          type: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: 'Paste',
          cancelButtonText: 'Cancel',
          showLoaderOnConfirm: true,
        });
      } catch (errSwal) {
        return;
      }

      let data;
      if (_.get(navigator, 'clipboard.readText')) {
        data = await navigator.clipboard.readText();
      } else {
        try {
          await this.$swal({
            title: 'Browser Does Not Support Direct Paste',
            input: 'textarea',
            inputPlaceholder: 'Table data',
            text: 'Your browser does not support directly pasting from ' +
              'the clipboard to our web application. Please paste your table ' +
              'data below so that it can be read by the table.',
            animation: true,
            showLoaderOnConfirm: true,
            showCancelButton: true,
            confirmButtonText: 'Upload Data',
            allowOutsideClick: false,
            preConfirm: async (dataArg) => {
              data = dataArg;
            },
          });
        } catch (err) {
          // Cancel
          return;
        }
      }
      await this.injectTableDataFromString(data, true);
    },

    // serializeCSV converts the present table into a CSV string.
    serializeCSV() {
      let list = this.tabulator.modules.download.generateExportList();

      // Remove the hamburgers row.
      list = _.map(list, (row) => Object({
        ...row,
        columns: row.columns.slice(1),
      }));

      let csvString;
      const setCsvString = (results) => {
        csvString = results;
      };
      this.tabulator.modules.download.downloaders.csv.call(
        this.tabulator,
        list,
        {
          bom: true,
          delimiter: ',',
        },
        setCsvString);

      return csvString;
    },

    uninjectTabulatorFn() {
      _.unset(window, `TABULATOR_TABLES.${this.tabulatorId}`);
    },

    async uploadCsv() {
      try {
        await this.$swal({
          title: 'Upload Table from File',
          text: 'Are you sure you want to upload a new table from a ' +
            'file? This action will erase the current table.',
          type: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: true,
          confirmButtonText: 'Upload',
          cancelButtonText: 'Cancel',
          showLoaderOnConfirm: true,
        });
      } catch (errSwal) {
        return;
      }
      const uploader = document.getElementById(this.fileUploadId);
      if (!uploader) return;
      uploader.click();
    },
  },

  async mounted() {
    this.headerContextMenu = [
      {
        disabled: !this.editable,
        label: 'Insert Column Left',
        action: async (e, column) => {
          const columnNew = await this.tabulator.addColumn(
            this.createColumnArgObj('New Column', this.headerContextMenu));
          columnNew.move(column, false);
          this.value = this.serializeCSV();
        },
      },
      {
        disabled: !this.editable,
        label: 'Insert Column Right',
        action: async (e, column) => {
          const columnNew = await this.tabulator.addColumn(
            this.createColumnArgObj('New Column', this.headerContextMenu));
          columnNew.move(column, true);
          this.value = this.serializeCSV();
        },
      },
      {
        disabled: !this.editable,
        label: 'Rename Column',
        action: async (e, column) => {
          const def = column.getDefinition();

          try {
            await this.$swal({
              title: `Rename Column '${def.title}'`,
              input: 'text',
              inputPlaceholder: 'New column name',
              text: 'Please enter a new column name.',
              animation: true,
              showLoaderOnConfirm: true,
              showCancelButton: true,
              confirmButtonText: 'Rename',
              allowOutsideClick: false,
              preConfirm: async (nameNew) => {
                await column.updateDefinition({ title: nameNew });
                this.value = this.serializeCSV();
              },
            });
          } catch (err) {
            // Sweet alert cancel
          }
        },
      },
      {
        disabled: !this.editable,
        label: 'Delete Column',
        action: async (e, column) => {
          const def = column.getDefinition();
          try {
            await this.$swal({
              title: 'Are you sure you want to delete the column ' +
                `'${def.title}'?`,
              text: 'The contents of this column will be deleted.',
              type: 'warning',
              allowOutsideClick: false,
              allowEscapeKey: false,
              showCancelButton: true,
              confirmButtonText: 'Delete Column',
              cancelButtonText: 'Cancel',
              showLoaderOnConfirm: true,
            });
            column.delete();
            this.value = this.serializeCSV();
          } catch (errSwal) {
            // Cancel
          }
        },
      },
      {
        separator: true,
      },
      {
        disabled: !this.editable,
        label: 'Paste Clipboard as Table',
        action: this.pasteClipboardAsTable,
      },
      {
        separator: true,
      },
      {
        disabled: false,
        label: 'Copy Table to Clipboard',
        action: this.copyToClipboard,
      },
      {
        separator: true,
      },
      {
        disabled: !this.editable,
        label: 'Upload Table from CSV',
        action: this.uploadCsv,
      },
      {
        disabled: false,
        label: 'Download Table as CSV',
        action: async (e, row) => {
          this.downloadCSVTable();
        },
      },
    ];

    if (this.$refs.tabulatorTable) {
      // Initialize Tabulator.
      if (!Tabulator) {
        const TabulatorImport = await import('tabulator-tables');
        Tabulator = TabulatorImport.default;
      }

      this.tabulator = new Tabulator(this.$refs.tabulatorTable, {
        columns: _.concat(
          [{
            rowHandle: true,
            formatter: 'handle',
            headerSort: false,
            frozen: true,
            width: 30,
            minWidth: 30,
          }],
          _.map(this.schema.values, (col) => this.createColumnArgObj(col,
            this.headerContextMenu))),
        clipboard: false,
        data: this.tableData,
        dataEdited: () => {
          this.value = this.serializeCSV();
        },
        dataSorted: async () => {
          await this.sleep(0);
          this.value = this.serializeCSV();
        },
        layoutColumnsOnNewData: true,
        movableColumns: true,
        movableRows: true,
        reactiveData: true,
        rowContextMenu: [
          {
            disabled: !this.editable,
            label: 'Add Row Above',
            action: async (e, row) => {
              const rows = await this.tabulator.addData([this.arrayToObject(
                this.columnIds, _.identity, () => '')]);
              rows[0].move(row, true);
              this.value = this.serializeCSV();
            },
          },
          {
            disabled: !this.editable,
            label: 'Add Row Below',
            action: async (e, row) => {
              const rows = await this.tabulator.addData([this.arrayToObject(
                this.columnIds, _.identity, () => '')]);
              rows[0].move(row, false);
              this.value = this.serializeCSV();
            },
          },
          {
            disabled: !this.editable,
            label: 'Delete Row',
            action: async (e, row) => {
              row.delete();
            },
          },
          {
            separator: true,
          },
          {
            disabled: !this.editable,
            label: 'Paste Clipboard as Table',
            action: this.pasteClipboardAsTable,
          },
          {
            disabled: !this.editable,
            label: 'Paste Clipboard as Cells',
            action: async (e, row) => {
              try {
                await this.$swal({
                  title: 'Paste Clipboard as Cells',
                  text: 'Are you sure you want to paste the current ' +
                    'clipboard into the cells in this table? All current ' +
                    'cells will be replaced.',
                  type: 'warning',
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  showCancelButton: true,
                  confirmButtonText: 'Paste',
                  cancelButtonText: 'Cancel',
                  showLoaderOnConfirm: true,
                });
              } catch (errSwal) {
                return;
              }

              let data;
              if (_.get(navigator, 'clipboard.readText')) {
                data = await navigator.clipboard.readText();
              } else {
                try {
                  await this.$swal({
                    title: 'Browser Does Not Support Direct Paste',
                    input: 'textarea',
                    inputPlaceholder: 'Table data',
                    text: 'Your browser does not support directly pasting ' +
                      'from the clipboard to our web application. Please ' +
                      'paste your table data below so that it can be read by ' +
                      'the table.',
                    animation: true,
                    showLoaderOnConfirm: true,
                    showCancelButton: true,
                    confirmButtonText: 'Upload Data',
                    allowOutsideClick: false,
                    preConfirm: async (dataArg) => {
                      data = dataArg;
                    },
                  });
                } catch (err) {
                  // Cancel
                  return;
                }
              }

              await this.injectTableDataFromString(data, false);
              this.$emit('emitTableUpdated', this.serializeCSV());
            },
          },
          {
            separator: true,
          },
          {
            disabled: false,
            label: 'Copy Table to Clipboard',
            action: this.copyToClipboard,
          },
          {
            separator: true,
          },
          {
            disabled: !this.editable,
            label: 'Upload Table from CSV',
            action: this.uploadCsv,
          },
          {
            disabled: false,
            label: 'Download Table as CSV',
            action: async (e, row) => {
              this.downloadCSVTable();
            },
          },
        ],
        selectable: false,
      });

      this.injectTabulatorIntoWindow();
      if (!_.get(this.model, this.schema.id, null)) {
        this.tableData = [this.arrayToObject(
          this.columnIds, _.identity, () => '')];
        await this.sleep(0);
        this.tabulator.replaceData(this.tableData);
        this.tabulator.redraw();
        this.value = this.serializeCSV();
      } else {
        this.injectTableDataFromString(_.get(this.model, this.schema.id, null),
          true);
      }
    }
  },
};
</script>

<style lang="scss">
  @import  "~tabulator-tables/dist/css/tabulator.min.css";
  @import "~assets/css/colors";

  .tabulator-table-wrapper {
    min-width: 300px;
  }

  .tabulator-file-upload-field {
    position: absolute;
    transform: translateY(-9999px);
    opacity: 0;
  }

  .tabulator-table-header-hint {
    font-size: 11px;
    float: left;
  }

  button.btn.btn-link.tabulator-add-row-button-bottom {
    color: var(--dm-success-color, $successcolor);
    float: right;
    cursor: pointer;
  }
</style>
